/**
 * Northern Shopper — visual toast API
 * NorthernShopper.Toast.show({ type, title, message, duration, broadcast })
 *
 * Types: .dev/types/ns-custom.toast.d.ts
 * Docs: .dev/docs/13-toast-api.md
 */
(function () {
	"use strict";

	var NS = (window.NorthernShopper = window.NorthernShopper || {});
	var STORAGE_KEY = "northernShopperToastEvent";
	var REGION_ID = "ns-custom-toast-region";
	var MAX_TOASTS = 5;
	var DEFAULT_DURATION = 4000;
	var LEAVE_MS = 200;
	var VALID_TYPES = {
		success: true,
		error: true,
		warning: true,
		info: true,
	};

	var region = null;
	var toasts = [];
	var idCounter = 0;

	function nextId() {
		idCounter += 1;
		return "ns-toast-" + Date.now() + "-" + idCounter;
	}

	function getRegion() {
		if (region && document.body && document.body.contains(region)) return region;
		if (!document.body) return null;

		region = document.getElementById(REGION_ID);
		if (!region) {
			region = document.createElement("div");
			region.id = REGION_ID;
			region.className = "ns-custom-toast-region";
			region.setAttribute("aria-live", "polite");
			region.setAttribute("aria-relevant", "additions");
			document.body.appendChild(region);
		}
		return region;
	}

	function normalize(options) {
		var opts = options || {};
		var type = VALID_TYPES[opts.type] ? opts.type : "info";
		var duration = typeof opts.duration === "number" ? opts.duration : DEFAULT_DURATION;
		if (duration < 0) duration = DEFAULT_DURATION;
		return {
			id: opts.id ? String(opts.id) : nextId(),
			type: type,
			title: opts.title != null && opts.title !== "" ? String(opts.title) : "",
			message: opts.message != null && opts.message !== "" ? String(opts.message) : "",
			duration: duration,
			broadcast: opts.broadcast !== false,
		};
	}

	function findEntry(id) {
		for (var i = 0; i < toasts.length; i++) {
			if (toasts[i].id === id) return toasts[i];
		}
		return null;
	}

	function createElement(opts) {
		var el = document.createElement("div");
		el.className = "ns-custom-toast ns-custom-toast--" + opts.type;
		el.setAttribute("role", opts.type === "error" ? "alert" : "status");
		el.dataset.toastId = opts.id;

		var body = document.createElement("div");
		body.className = "ns-custom-toast__body";

		if (opts.title) {
			var title = document.createElement("p");
			title.className = "ns-custom-toast__title";
			title.textContent = opts.title;
			body.appendChild(title);
		}

		if (opts.message) {
			var message = document.createElement("p");
			message.className = "ns-custom-toast__message";
			message.textContent = opts.message;
			body.appendChild(message);
		}

		var close = document.createElement("button");
		close.type = "button";
		close.className = "ns-custom-toast__close";
		close.setAttribute("aria-label", "Dismiss notification");
		close.innerHTML =
			'<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="ns-custom-toast__close-icon" fill="none" viewBox="0 0 24 24"><path d="M18.75 5.135L5.25 18.854" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M18.75 18.854L5.25 5.135" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
		close.addEventListener("click", function () {
			dismiss(opts.id);
		});

		el.appendChild(body);
		el.appendChild(close);
		return el;
	}

	function startTimer(entry) {
		if (!entry.duration || entry.remaining <= 0) return;
		entry.startedAt = Date.now();
		entry.timer = setTimeout(function () {
			dismiss(entry.id);
		}, entry.remaining);
	}

	function pauseTimer(entry) {
		if (!entry.timer) return;
		clearTimeout(entry.timer);
		entry.timer = null;
		entry.remaining = Math.max(0, entry.remaining - (Date.now() - entry.startedAt));
	}

	function resumeTimer(entry) {
		if (entry.timer) return;
		if (entry.remaining <= 0) {
			dismiss(entry.id);
			return;
		}
		startTimer(entry);
	}

	function broadcast(opts) {
		try {
			localStorage.setItem(
				STORAGE_KEY,
				JSON.stringify({
					id: opts.id,
					type: opts.type,
					title: opts.title,
					message: opts.message,
					duration: opts.duration,
					ts: Date.now(),
				}),
			);
		} catch (e) {
			/* ignore quota / private mode */
		}
	}

	function enforceCap() {
		while (toasts.length > MAX_TOASTS) {
			dismiss(toasts[0].id);
		}
	}

	function show(options) {
		var opts = normalize(options);
		if (!opts.title && !opts.message) return null;
		if (findEntry(opts.id)) return opts.id;

		var host = getRegion();
		if (!host) return null;

		var el = createElement(opts);
		host.appendChild(el);

		var entry = {
			id: opts.id,
			el: el,
			timer: null,
			remaining: opts.duration,
			startedAt: 0,
			duration: opts.duration,
			leaving: false,
		};
		toasts.push(entry);

		el.addEventListener("mouseenter", function () {
			pauseTimer(entry);
		});
		el.addEventListener("mouseleave", function () {
			resumeTimer(entry);
		});

		startTimer(entry);
		enforceCap();

		if (opts.broadcast) {
			broadcast(opts);
		}

		return opts.id;
	}

	function dismiss(id) {
		var entry = findEntry(id);
		if (!entry || entry.leaving) return;

		entry.leaving = true;
		if (entry.timer) {
			clearTimeout(entry.timer);
			entry.timer = null;
		}

		var index = toasts.indexOf(entry);
		if (index !== -1) toasts.splice(index, 1);

		if (!entry.el) return;
		entry.el.classList.add("ns-custom-toast--leaving");
		setTimeout(function () {
			if (entry.el && entry.el.parentNode) {
				entry.el.parentNode.removeChild(entry.el);
			}
		}, LEAVE_MS);
	}

	function dismissAll() {
		var ids = toasts.map(function (entry) {
			return entry.id;
		});
		ids.forEach(dismiss);
	}

	function helper(type) {
		return function (titleOrOptions, message, extra) {
			if (titleOrOptions && typeof titleOrOptions === "object") {
				var opts = {};
				for (var key in titleOrOptions) {
					if (Object.prototype.hasOwnProperty.call(titleOrOptions, key)) {
						opts[key] = titleOrOptions[key];
					}
				}
				opts.type = type;
				return show(opts);
			}
			extra = extra || {};
			return show({
				type: type,
				title: titleOrOptions || "",
				message: message || "",
				duration: extra.duration,
				broadcast: extra.broadcast,
			});
		};
	}

	function onStorage(event) {
		if (event.key !== STORAGE_KEY || !event.newValue) return;
		try {
			var payload = JSON.parse(event.newValue);
			show({
				id: payload.id,
				type: payload.type,
				title: payload.title,
				message: payload.message,
				duration: payload.duration,
				broadcast: false,
			});
		} catch (e) {
			/* ignore malformed payloads */
		}
	}

	window.addEventListener("storage", onStorage);

	NS.Toast = {
		STORAGE_KEY: STORAGE_KEY,
		show: show,
		dismiss: dismiss,
		dismissAll: dismissAll,
		success: helper("success"),
		error: helper("error"),
		warning: helper("warning"),
		info: helper("info"),
	};

	function init() {
		getRegion();
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", init);
	} else {
		init();
	}
})();
